import { A11yModule } from '@angular/cdk/a11y';
import { BreakpointObserver } from '@angular/cdk/layout';
import { FlexibleConnectedPositionStrategyOrigin, OverlayModule } from '@angular/cdk/overlay';
import { FlatTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  model,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Signal,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeModule } from '@angular/material/tree';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';

import { RouteCodeEnum } from '@models/menu';
import { ModuleIconComponent } from '@shared/components/module-icon/module-icon.component';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { CipoFormModule } from '@shared/modules';
import { ContextStore } from '@signal-store/context.store';
import { UserStore } from '@signal-store/index';
import {
  ContextContract,
  ContextInfo,
  ContextInfoPart,
  ContextModule,
  ContextProgram,
  ContextProject,
  ContextTree,
  ShowTypeEnum,
} from '@system/context/context.model';
import { ContextService } from '@system/context/context.service';
import { BREAKPOINTS_MEDIA_QUERY } from 'src/app-breakpoints.module';

const PROGRAM_MODULE_ID = -23;
const PROJECT_MODULE_ID = -10;
const CONTRACT_MODULE_ID = -1;

interface ContextNode {
  context: ContextInfoPart;
  type: 'all' | 'program' | 'project' | 'contract';
  selected?: boolean;
  children?: ContextNode[];
}

interface ContextFlatNode {
  expandable: boolean;
  context: ContextInfoPart;
  type: 'all' | 'program' | 'project' | 'contract';
  selected: boolean;
  level: number;
}

@Component({
  selector: 'cipo-context-view',
  templateUrl: './context-view.component.html',
  styleUrls: ['./context-view.component.scss'],
  imports: [
    A11yModule,
    CommonModule,
    OverlayModule,
    CipoFormModule,
    ModuleIconComponent,
    NgxSkeletonLoaderModule,
    MatTreeModule,
  ],
})
export class ContextViewComponent implements OnInit, OnDestroy {
  userStore = inject(UserStore);
  contextStore = inject(ContextStore);

  context = model<ContextInfo>(undefined);

  @Output() contextData = new EventEmitter<ContextTree>();

  @ViewChild('contextContent', { static: true }) contextContentChild: ElementRef;
  @ViewChild('searchInput') searchInput: ElementRef;
  @ViewChild('contextScrollContainer') contextScrollContainer: ElementRef<HTMLElement>;
  @ViewChildren('contextNodeElement') contextNodeElements: QueryList<ElementRef<HTMLElement>>;

  private intersectionObserver: IntersectionObserver;
  private stickyUpdateTimeout: any;

  lastContextPart: Signal<ContextInfoPart | undefined> = computed(
    () =>
      this.context()?.contract ||
      this.context()?.project ||
      this.context()?.program ||
      (this.useAll() ? this.contextInfoPartAll : undefined),
  );
  isContextOverlayOpen = signal(false);
  isLoadingTree = signal(false);
  hostWidth = signal(0);
  contextContentOrigin = signal<FlexibleConnectedPositionStrategyOrigin>({ x: 0, y: 0 });
  showClosed = signal(false);
  showType = signal<ShowTypeEnum>(ShowTypeEnum.Active);
  contractsNumberWithoutCurrentFilter = signal(0);
  contractsNumberWithCurrentFilter = signal(0);
  isContextDefined = computed(
    () =>
      this.context() && (this.context()?.contract?.id || this.context()?.project?.id || this.context()?.program?.id),
  );
  activeMenuCode = computed(() => this.userStore.menu.activeMenuItem()?.code ?? RouteCodeEnum.Dashboard);
  useOnlyContract = computed(
    () => ![RouteCodeEnum.FileContractManager, RouteCodeEnum.Dashboard].includes(this.activeMenuCode()),
  );
  useAll = computed(() => this.activeMenuCode() === RouteCodeEnum.Dashboard);
  ignoreContextPermission = computed(() => this.activeMenuCode() === RouteCodeEnum.Dashboard);

  SHOW_TYPE = ShowTypeEnum;
  observer: ResizeObserver;
  loadingTreeSkeletonArray = [...Array(7).keys()];
  searchControl = new FormControl('');
  scrollIntoViewTimeout = undefined;
  contextTree: ContextTree;
  showOverlayFullWidth = false;
  refresh = this.userStore.reloadContext;

  contextInfoPartAll = {
    id: -1,
    name: 'All',
    moduleColor: '#000000',
    moduleIconId: 50,
    hasPermission: true,
  } as ContextInfoPart;

  treeControl = new FlatTreeControl<ContextFlatNode>(
    node => node.level,
    node => node.expandable,
  );

  treeFlattener = new MatTreeFlattener(
    (node: ContextNode, level: number) => ({
      expandable: !!node.children && node.children.length > 0,
      context: node.context,
      type: node.type,
      selected: node.selected,
      level: level,
    }),
    node => node.level,
    node => node.expandable,
    node => node.children,
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener, []);
  dataSourceOriginalData: ContextNode[] = [];
  hasChild = (_: number, node: ContextFlatNode) => node.expandable;
  setContextAfterLoadingTree = false;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private elementRef: ElementRef<HTMLElement>,
    private destroyRef: DestroyRef,
    private cdRef: ChangeDetectorRef,
    private contextService: ContextService,
  ) {
    breakpointObserver
      .observe([BREAKPOINTS_MEDIA_QUERY.lt_lg])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.showOverlayFullWidth = result.matches;
      });

    effect(() => {
      const ctx = this.context();
      this.attachMissingPartsWhenNotSet(ctx);
      this.updateOriginalData(ctx);
    });
    effect(() => {
      const refresh = this.refresh();
      if (this.isContextOverlayOpen() || refresh) {
        this.loadContextTree(refresh);
        this.userStore.setReloadContext(false);
      }
    });
    effect(() => {
      if (this.isLoadingTree()) {
        this.searchControl.disable();
      } else {
        this.searchControl.enable();
      }
    });
    effect(() => {
      if (this.isContextOverlayOpen()) {
        const width = this.contextContentChild?.nativeElement?.clientWidth ?? 0;
        this.setContextContentWidth(width);
        this.setContextContentOrigin(this.contextContentChild);
        this.searchInput?.nativeElement?.focus();
      }
    });

    this.searchControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(DEBOUNCE_TIMES.default),
        distinctUntilChanged(),
        filter(_ => this.isContextOverlayOpen()),
      )
      .subscribe(value => this.search(value));
  }

  private contextInfoPartIsEqual(part1: ContextInfoPart, part2: ContextInfoPart): boolean {
    return (
      this.areNumbersEqual(part1.id, part2.id) &&
      part1.name === part2.name &&
      part1.no === part2.no &&
      this.areNumbersEqual(part1.entityInstanceId, part2.entityInstanceId) &&
      this.areNumbersEqual(part1.moduleId, part2.moduleId) &&
      this.areNumbersEqual(part1.currencyId, part2.currencyId) &&
      part1.isArchived === part2.isArchived &&
      part1.isClosed === part2.isClosed &&
      part1.isUsed === part2.isUsed &&
      part1.hasPermission === part2.hasPermission
    );
  }

  private attachMissingPartsWhenNotSet(ctx: ContextInfo) {
    // this happens:
    //   - when user does a switch between a place when useOnlyContract is set to true then to false
    //      i.e. User is on My drive where only contracts are shown, then moves to Project Files where full hierarchy is shown
    //   - when user does a switch to a new contract, project or program but does not have the full context info, only partial info like id or entityInstanceId

    if (ctx && !this.contextTree) {
      // context has been set before loading the context, most likely due to a route change or loaded an external link
      this.setContextAfterLoadingTree = true;
      return;
    }
    if (!this.contextTree || !ctx) {
      return;
    }

    let changed = false;
    if (ctx.contract && (!ctx.program || !ctx.project)) {
      const program = (this.contextTree.programs || []).find(p =>
        (p.projects || []).some(j =>
          (j.contracts || []).some(
            c => c.entityInstanceId === ctx.contract.entityInstanceId * 1 || c.id === ctx.contract.id * 1,
          ),
        ),
      );
      const project = (program?.projects || []).find(j =>
        (j.contracts || []).some(
          c => c.entityInstanceId === ctx.contract.entityInstanceId * 1 || c.id === ctx.contract.id * 1,
        ),
      );
      const contract = (project?.contracts || []).find(
        c => c.entityInstanceId === ctx.contract.entityInstanceId * 1 || c.id === ctx.contract.id * 1,
      );
      if (!ctx.program && program?.entityInstanceId) {
        ctx.program = this.programToContextInfoPart(program, this.contextTree.modules);
        changed = true;
      }
      if (!ctx.project && project?.entityInstanceId) {
        ctx.project = this.projectToContextInfoPart(project, this.contextTree.modules);
        changed = true;
      }
      if (contract?.entityInstanceId) {
        const newContract = this.contractToContextInfoPart(contract, this.contextTree.modules);
        if (!this.contextInfoPartIsEqual(ctx.contract, newContract)) {
          ctx.contract = newContract;
          changed = true;
        }
      }
    } else if (ctx.project && !ctx.program) {
      const program = (this.contextTree.programs || []).find(p =>
        (p.projects || []).some(
          j => j.entityInstanceId === ctx.project.entityInstanceId * 1 || j.id === ctx.project.id * 1,
        ),
      );
      const project = (program?.projects || []).find(
        j => j.entityInstanceId === ctx.project.entityInstanceId * 1 || j.id === ctx.project.id * 1,
      );
      if (!ctx.program && program?.entityInstanceId) {
        ctx.program = this.programToContextInfoPart(program, this.contextTree.modules);
        changed = true;
      }
      if (project?.entityInstanceId) {
        const newProject = this.projectToContextInfoPart(project, this.contextTree.modules);
        if (!this.contextInfoPartIsEqual(ctx.project, newProject)) {
          ctx.project = newProject;
          changed = true;
        }
      }
    } else if (ctx.program) {
      const program = (this.contextTree.programs || []).find(
        p => p.entityInstanceId === ctx.program.entityInstanceId * 1 || p.id === ctx.program.id * 1,
      );
      if (program?.entityInstanceId) {
        const newProgram = this.programToContextInfoPart(program, this.contextTree.modules);
        if (!this.contextInfoPartIsEqual(ctx.program, newProgram)) {
          ctx.program = newProgram;
          changed = true;
        }
      }
    }

    if (changed) {
      this.context.set({ ...ctx });
    }
  }

  ngOnInit(): void {
    this.observer = new ResizeObserver(entries => {
      const entry = entries[0];
      this.setContextContentWidth(entry.contentRect.width);
      this.cdRef.markForCheck();
      this.cdRef.detectChanges();
    });
    this.observer.observe(this.elementRef.nativeElement);

    // has to be loaded first
    // first reason is because there is logice based on the number of contract, if there is only one or more contracts
    // second reason is because the list of contracts is needed later even if the overlay is never shown
    this.loadContextTree();
  }

  ngOnDestroy() {
    this.observer?.disconnect();
    this.intersectionObserver?.disconnect();
    if (this.stickyUpdateTimeout) {
      clearTimeout(this.stickyUpdateTimeout);
    }
  }

  onEscape(): void {
    if (this.isContextOverlayOpen()) {
      this.isContextOverlayOpen.set(false);
      setTimeout(() => this.contextContentChild?.nativeElement?.focus());
    }
  }

  nodeClick(node: ContextFlatNode): void {
    if (!(this.ignoreContextPermission() || node.context.hasPermission)) {
      return;
    }

    const ctx = this.getContextFromNode(node);

    // clear current selection and mark clicked node as selected
    this.treeControl.dataNodes.forEach(n => (n.selected = false));
    node.selected = true;

    this.context.set(ctx);
    this.isContextOverlayOpen.set(false);
    setTimeout(() => this.contextContentChild?.nativeElement?.focus());
  }

  toggleContextOverlay(): void {
    if (this.isLoadingTree()) {
      return;
    }
    this.isContextOverlayOpen.set(!this.isContextOverlayOpen());
  }

  setShowType(showType: ShowTypeEnum): void {
    this.showType.set(showType);
    this.loadContextTree();
  }

  private getContextFromNode(node: ContextFlatNode): ContextInfo | undefined {
    if (!node) {
      return undefined;
    }
    return this.getContextFromNodeByEntityInstanceId(node.context?.entityInstanceId) || {};
  }

  private getContextFromNodeByEntityInstanceId(entityInstanceId: number): ContextInfo | undefined {
    if (!entityInstanceId || (this.contextTree?.programs || []).length === 0) {
      return undefined;
    }
    const ctx: ContextInfo = {};
    for (const program of this.contextTree.programs) {
      if (program.entityInstanceId === entityInstanceId) {
        ctx.program = this.programToContextInfoPart(program, this.contextTree.modules);
        return ctx.program ? ctx : undefined;
      }
      for (const project of program.projects || []) {
        if (project.entityInstanceId === entityInstanceId) {
          ctx.program = this.programToContextInfoPart(program, this.contextTree.modules);
          ctx.project = this.projectToContextInfoPart(project, this.contextTree.modules);
          return ctx.project ? ctx : undefined;
        }
        for (const contract of project.contracts || []) {
          if (contract.entityInstanceId === entityInstanceId) {
            ctx.program = this.programToContextInfoPart(program, this.contextTree.modules);
            ctx.project = this.projectToContextInfoPart(project, this.contextTree.modules);
            ctx.contract = this.contractToContextInfoPart(contract, this.contextTree.modules);
            return ctx.contract ? ctx : undefined;
          }
        }
      }
    }
    return undefined;
  }

  private findParentNode(node: ContextFlatNode | undefined): ContextFlatNode | undefined {
    if (!node || node.level === 0) {
      return undefined;
    }
    const index = this.treeControl.dataNodes.indexOf(node);
    const parent = [...this.treeControl.dataNodes.filter((_, i) => i < index)]
      .reverse()
      .find(n => n.level === node.level - 1);
    return parent;
  }

  private loadContextTree(forceLoad?: boolean): void {
    this.isLoadingTree.set(true);

    if (forceLoad || !this.dataSourceOriginalData.length) {
      this.contextService
        .getContextTree()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(tree => {
          this.contextTree = tree;
          this.contextStore.set(tree);
          this.contextData.emit(tree);
          this.contractsNumberWithoutCurrentFilter.set(this.getContractsNumberWithFilterTypeAll(tree));
          this.setContextTree();
        });
    } else {
      this.setContextTree();
    }
  }

  private getAllProjects(tree: ContextTree): ContextProject[] {
    const projects: ContextProject[] = (tree?.programs || []).reduce((acc, program) => {
      acc.push(...(program.projects || []));
      return acc;
    }, [] as ContextProject[]);
    return projects;
  }

  private getAllContracts(tree: ContextTree): ContextContract[] {
    const contracts: ContextContract[] = this.getAllProjects(tree).reduce((acc, project) => {
      acc.push(...(project.contracts || []));
      return acc;
    }, [] as ContextContract[]);
    return contracts;
  }

  private getContractsNumberWithFilterTypeAll(tree: ContextTree): number {
    const filteredTree = this.filteredContextTree(tree, ShowTypeEnum.All);
    const contracts = this.getAllContracts(filteredTree);
    return contracts.length;
  }

  private setContextTree(): void {
    const filteredTree = this.filteredContextTree(this.contextTree, this.showType());
    const contracts = this.getAllContracts(filteredTree);
    this.contractsNumberWithCurrentFilter.set(contracts.length);

    if (this.useOnlyContract()) {
      // get all the contracts from all programs and projects
      this.dataSourceOriginalData = this.contractsToContextNode(contracts, filteredTree?.modules);
    } else {
      const nodes = this.programsToContextNode(filteredTree?.programs, filteredTree?.modules);

      if (this.useAll()) {
        nodes.unshift({ type: 'all', context: this.contextInfoPartAll, selected: false } as ContextNode);
      }
      this.dataSourceOriginalData = nodes;
    }

    if (this.setContextAfterLoadingTree) {
      this.setContextAfterLoadingTree = false;
      this.attachMissingPartsWhenNotSet(this.context());
    }

    const shouldKeepAll = this.useAll() && this.lastContextPart()?.id === this.contextInfoPartAll.id;
    // set the initial context when there is no context provided and we have at least a contract
    if (!this.isContextDefined() && this.contractsNumberWithCurrentFilter() >= 1 && !shouldKeepAll) {
      this.setFirstContractAsCurrentContext();
    }

    this.search(this.searchControl.value);
  }

  private setFirstContractAsCurrentContext() {
    const program = this.contextTree.programs.length ? this.contextTree.programs[0] : undefined;
    const project = program.projects.length ? program.projects[0] : undefined;
    const contract = project.contracts.length ? project.contracts[0] : undefined;
    if (contract) {
      this.context.set({
        program: this.programToContextInfoPart(program, this.contextTree.modules),
        project: this.projectToContextInfoPart(project, this.contextTree.modules),
        contract: this.contractToContextInfoPart(contract, this.contextTree.modules),
      });
    }
  }

  private updateOriginalData(context: ContextInfo): void {
    (this.contextTree?.programs || []).forEach(program => {
      if (program.entityInstanceId === context.program?.entityInstanceId) {
        program.currencyId = context.program?.currencyId;
        program.hasPermission = context.program?.hasPermission;
        program.name = context.program?.name;
        program.no = context.program?.no;
      }
      (program.projects || []).forEach(project => {
        if (project.entityInstanceId === context.project?.entityInstanceId) {
          project.currencyId = context.project?.currencyId;
          project.hasPermission = context.project?.hasPermission;
          project.name = context.project?.name;
          project.no = context.project?.no;
        }
        (project.contracts || []).forEach(contract => {
          if (contract.entityInstanceId === context.contract?.entityInstanceId) {
            contract.currencyId = context.contract?.currencyId;
            contract.hasPermission = context.contract?.hasPermission;
            contract.name = context.contract?.name;
            contract.no = context.contract?.no;
            contract.isArchived = context.contract?.isArchived;
            contract.isClosed = context.contract?.isClosed;
            contract.isUsed = context.contract?.isUsed;
            contract.canEdit = context.contract?.canEdit;
          }
        });
      });
    });
  }

  private scrollIntoView(node: ContextFlatNode): void {
    const nodeElement = this.getNodeElement(node);
    if (!nodeElement) {
      return;
    }

    const scrollContainer = this.contextScrollContainer?.nativeElement ?? null;
    if (!scrollContainer) {
      return;
    }

    // Calculate the required scroll position
    const nodeHeight = nodeElement.offsetHeight;
    const index = this.treeControl.dataNodes.indexOf(node);
    const scrollPosition = Math.max(0, nodeHeight * (index - node.level));

    // Move scrolling to the calculated position
    scrollContainer.scrollTo({ top: scrollPosition, behavior: 'auto' });
  }

  private getNodeElement(node: ContextFlatNode): HTMLElement | null {
    if (this.contextNodeElements) {
      const dataSource = this.treeControl.dataNodes;
      const index = dataSource.indexOf(node);
      if (index !== -1 && this.contextNodeElements.length > index) {
        return this.contextNodeElements.get(index)?.nativeElement ?? null;
      }
    }
    return null;
  }

  private programsToContextNode(programs: ContextProgram[], modules: ContextModule[]): ContextNode[] {
    const programNodesWithId = (programs || [])
      .filter(program => !!program.id)
      .map(
        program =>
          ({
            context: this.programToContextInfoPart(program, modules),
            type: 'program',
            children: this.projectsToContextNode(program?.projects, modules),
          }) as ContextNode,
      );
    const programNodesWithoutId = this.projectsToContextNode(
      (programs || []).filter(program => !program.id).flatMap(p => p.projects),
      modules,
    );
    return [...programNodesWithId, ...programNodesWithoutId].filter(node => !!node).sort(this.sortContextNodeAsc);
  }

  private projectsToContextNode(projects: ContextProject[], modules: ContextModule[]): ContextNode[] {
    return (projects || [])
      .map(project =>
        (project?.contracts || []).length > 1
          ? ({
              context: this.projectToContextInfoPart(project, modules),
              type: 'project',
              children: this.contractsToContextNode(project?.contracts, modules),
            } as ContextNode)
          : (project?.contracts || []).length === 1
            ? this.contractsToContextNode(project?.contracts, modules)[0]
            : null,
      )
      .filter(node => !!node)
      .sort(this.sortContextNodeAsc);
  }

  private contractsToContextNode(contracts: ContextContract[], modules: ContextModule[]): ContextNode[] {
    return (contracts || [])
      .map(
        contract =>
          ({
            context: this.contractToContextInfoPart(contract, modules),
            type: 'contract',
          }) as ContextNode,
      )
      .sort(this.sortContextNodeAsc);
  }

  private filteredContextTree(tree: ContextTree, showType: ShowTypeEnum): ContextTree {
    if (!tree) {
      return tree;
    }
    const contractFilter = (contract: ContextContract): boolean =>
      (!contract.isArchived && !contract.isClosed && showType === ShowTypeEnum.Active) ||
      (contract.isClosed && showType === ShowTypeEnum.OnlyClosed) ||
      (contract.isArchived && showType === ShowTypeEnum.OnlyArchived) ||
      showType === ShowTypeEnum.All;
    return {
      ...tree,
      programs: [
        ...(tree.programs || []).map(program => ({
          ...program,
          projects: [
            ...(program.projects || []).map(project => ({
              ...project,
              contracts: (project.contracts || []).filter(contractFilter),
            })),
          ],
        })),
      ],
    };
  }

  private partToContextInfoPart(
    part: ContextContract | ContextProject | ContextProgram,
    module: ContextModule,
  ): ContextInfoPart {
    return {
      id: part.id,
      name: part.name,
      no: part.no,
      entityInstanceId: part.entityInstanceId,
      moduleId: part.moduleId,
      moduleAbbr: module.abbr,
      moduleColor: module.color,
      moduleIconId: module.iconId,
      currencyId: part.currencyId,
      hasPermission: part.hasPermission,
      isArchived: (part as ContextContract)?.isArchived,
      isClosed: (part as ContextContract)?.isClosed,
      isUsed: (part as ContextContract)?.isUsed,
      canEdit: (part as ContextContract)?.canEdit,
    };
  }

  private contractToContextInfoPart(part: ContextContract, modules: ContextModule[]): ContextInfoPart {
    const module = (modules || []).find(module => module.moduleId === CONTRACT_MODULE_ID);
    return this.partToContextInfoPart(part, module);
  }

  private projectToContextInfoPart(part: ContextProject, modules: ContextModule[]): ContextInfoPart {
    const module = (modules || []).find(module => module.moduleId === PROJECT_MODULE_ID);
    return this.partToContextInfoPart(part, module);
  }

  private programToContextInfoPart(part: ContextProgram, modules: ContextModule[]): ContextInfoPart {
    const module = (modules || []).find(module => module.moduleId === PROGRAM_MODULE_ID);
    return this.partToContextInfoPart(part, module);
  }

  private sortContextNodeAsc(a: ContextNode, b: ContextNode): number {
    // Compare by: program, then project, then contract
    const typeOrder = { program: 1, project: 2, contract: 3, all: 0 };
    const typeComparison = (typeOrder[a.type] || 0) - (typeOrder[b.type] || 0);
    if (typeComparison !== 0) {
      return typeComparison;
    }

    // Archived and closed last (but still sorted by no and name within themselves)
    const aIsArchivedOrClosed = a.context.isArchived || a.context.isClosed ? 1 : 0;
    const bIsArchivedOrClosed = b.context.isArchived || b.context.isClosed ? 1 : 0;
    const archivedClosedComparison = aIsArchivedOrClosed - bIsArchivedOrClosed;
    if (archivedClosedComparison !== 0) {
      return archivedClosedComparison;
    }

    // Compare by: no (ascending)
    const noComparison = (a?.context?.no || '').localeCompare(b?.context?.no || '');
    if (noComparison !== 0) {
      return noComparison;
    }

    // Compare by: name (ascending)
    const nameComparison = (a?.context?.name || '').localeCompare(b?.context?.name || '');
    return nameComparison;
  }

  private setContextContentOrigin(target: ElementRef<HTMLElement>): void {
    const rect = target.nativeElement.parentElement.getBoundingClientRect();
    this.contextContentOrigin.set({ x: this.showOverlayFullWidth ? 4 : rect.left, y: rect.bottom });
  }

  private setContextContentWidth(width: number): void {
    if (this.showOverlayFullWidth) {
      this.hostWidth.set(window.innerWidth - 32);
    } else {
      this.hostWidth.set(width);
    }
  }

  private search(value: string) {
    this.isLoadingTree.set(true);

    this.dataSource.data = [...this.dataSourceOriginalData].map(d => this.filterContextNode(d, value)).filter(n => !!n);
    this.treeControl.expandAll();

    const contextEntityInstanceId =
      this.context()?.contract?.entityInstanceId ??
      this.context()?.project?.entityInstanceId ??
      this.context()?.program?.entityInstanceId;
    const selectedNode = this.treeControl.dataNodes.find(n => contextEntityInstanceId === n.context.entityInstanceId);
    if (selectedNode) {
      selectedNode.selected = true;
      this.cdRef.detectChanges();
    }

    this.isLoadingTree.set(false);

    if (selectedNode) {
      if (this.scrollIntoViewTimeout) {
        clearTimeout(this.scrollIntoViewTimeout);
      }
      this.scrollIntoViewTimeout = setTimeout(() => {
        this.scrollIntoView(selectedNode);
      }, 100);
    }

    // Setup intersection observer for conditional sticky behavior
    this.setupStickyObserver();
  }

  private setupStickyObserver(): void {
    this.intersectionObserver?.disconnect();

    // Wait for DOM to be fully rendered before observing
    setTimeout(() => {
      const scrollContainer = this.contextScrollContainer?.nativeElement;
      if (!scrollContainer) return;

      // Create observer that triggers on any visibility change
      this.intersectionObserver = new IntersectionObserver(
        () => {
          // Debounce updates to prevent flickering from rapid scroll events
          if (this.stickyUpdateTimeout) clearTimeout(this.stickyUpdateTimeout);

          this.stickyUpdateTimeout = setTimeout(() => {
            this.updateStickyParents(scrollContainer);
          }, 10);
        },
        { root: scrollContainer, threshold: 0, rootMargin: '0px' },
      );

      // Observe all tree nodes for intersection changes
      const allNodes = scrollContainer.querySelectorAll('.mat-tree-node');
      allNodes.forEach(node => this.intersectionObserver.observe(node));
    }, 150);
  }

  private updateStickyParents(scrollContainer: HTMLElement): void {
    const allTreeNodes = scrollContainer.querySelectorAll('.mat-tree-node');
    const parentsToActivate = new Set<number>();
    const containerRect = scrollContainer.getBoundingClientRect();

    // Find all visible child nodes and collect their parent indices
    allTreeNodes.forEach((nodeElement, dataIndex) => {
      const node = this.treeControl.dataNodes[dataIndex];
      // Skip level 0 nodes (top-level parents) - they should only be sticky when their children are visible
      if (!node || node.level === 0) return;

      // If this child node is visible in viewport, mark all its parents for sticky positioning
      if (this.isElementVisible(nodeElement as HTMLElement, containerRect)) {
        this.addParentIndicesToSet(node, parentsToActivate);
      }
    });

    // Apply sticky-active class only to parents with visible children
    allTreeNodes.forEach((element, index) => {
      element.classList.toggle('sticky-active', parentsToActivate.has(index));
    });
  }

  private isElementVisible(element: HTMLElement, containerRect: DOMRect): boolean {
    const rect = element.getBoundingClientRect();
    // Check if element intersects with container's vertical bounds
    return rect.top < containerRect.bottom && rect.bottom > containerRect.top;
  }

  private addParentIndicesToSet(node: ContextFlatNode, parentsSet: Set<number>): void {
    // Walk up the tree hierarchy and add all parent indices to the set
    let currentParent = this.findParentNode(node);
    while (currentParent) {
      const parentIndex = this.treeControl.dataNodes.indexOf(currentParent);
      if (parentIndex !== -1) parentsSet.add(parentIndex);
      currentParent = this.findParentNode(currentParent);
    }
  }

  private filterContextNode(node: ContextNode, searchText: string): ContextNode | undefined {
    const newNode = { ...node };
    if (newNode.children && newNode.children.length) {
      newNode.children = newNode.children.map(n => this.filterContextNode(n, searchText)).filter(n => !!n);
      if (newNode.children.length) {
        return newNode;
      }
    }
    if (this.filterContextPart(newNode.context, searchText)) {
      return newNode;
    }
    return undefined;
  }

  private filterContextPart(ctx: ContextInfoPart, searchText: string): boolean {
    return `${ctx.no}${ctx.no ? ' - ' : ''}${ctx.name}`.toLowerCase().includes(searchText.toLowerCase());
  }

  private areNumbersEqual(a?: number, b?: number): boolean {
    return (Number.isNaN(+a) && Number.isNaN(+b)) || +a === +b;
  }
}
